import React, { useState } from 'react';

/**
 * AUTH DISSECTION
 *
 * Understanding authentication from surface to core
 * Every layer, every pattern, every decision
 */

export default function AuthDissection() {
  const [currentLayer, setCurrentLayer] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const layers = [
    {
      id: 0,
      name: 'Surface: What Users See',
      color: 'text-blue-400',
      content: {
        visual: `
[Login Button] → [Login Form] → [Dashboard]
                      ↓
                [Error Message]`,
        components: [
          { name: 'Login Form', purpose: 'Collect credentials' },
          { name: 'Signup Form', purpose: 'Create account' },
          { name: 'Logout Button', purpose: 'End session' },
          { name: 'Protected Route', purpose: 'Block unauthorized' }
        ],
        userFlow: [
          '1. User clicks "Login"',
          '2. Enters email/password',
          '3. Clicks submit',
          '4. Sees dashboard OR error'
        ]
      }
    },
    {
      id: 1,
      name: 'Layer 1: State Management',
      color: 'text-green-400',
      content: {
        states: {
          unauthenticated: { user: null, token: null },
          authenticating: { user: null, token: null, loading: true },
          authenticated: { user: '{ id, email, name }', token: 'jwt-xyz' },
          error: { user: null, token: null, error: 'Invalid credentials' }
        },
        transitions: `
unauthenticated → authenticating → authenticated
                      ↓
                    error → unauthenticated`,
        storage: [
          'Memory: Current session state',
          'LocalStorage: Persist across refreshes',
          'Cookie: HttpOnly for security',
          'SessionStorage: Tab-specific auth'
        ]
      }
    },
    {
      id: 2,
      name: 'Layer 2: API Communication',
      color: 'text-yellow-400',
      content: {
        endpoints: [
          {
            method: 'POST',
            path: '/api/auth/login',
            body: '{ email, password }',
            response: '{ token, user }',
            errors: ['401: Invalid credentials', '429: Rate limited', '500: Server error']
          },
          {
            method: 'POST',
            path: '/api/auth/signup',
            body: '{ email, password, name }',
            response: '{ token, user }',
            errors: ['409: Email exists', '400: Invalid email', '400: Weak password']
          },
          {
            method: 'POST',
            path: '/api/auth/refresh',
            body: '{ refreshToken }',
            response: '{ accessToken }',
            errors: ['401: Token expired', '403: Invalid token']
          },
          {
            method: 'POST',
            path: '/api/auth/logout',
            body: '{ token }',
            response: '{ success }',
            errors: []
          }
        ],
        headers: {
          'Authorization': 'Bearer <token>',
          'X-Refresh-Token': '<refresh-token>',
          'X-CSRF-Token': '<csrf-token>'
        }
      }
    },
    {
      id: 3,
      name: 'Layer 3: Security Patterns',
      color: 'text-red-400',
      content: {
        tokenStrategy: {
          JWT: {
            pros: ['Stateless', 'Scalable', 'Contains user data'],
            cons: ['Cant revoke', 'Size', 'Exposed data'],
            implementation: `
// Generate
jwt.sign({ userId, email }, SECRET, { expiresIn: '1h' })

// Verify
jwt.verify(token, SECRET)`
          },
          SessionID: {
            pros: ['Revokable', 'Small', 'Secure'],
            cons: ['Stateful', 'Database lookup', 'Scale issues'],
            implementation: `
// Generate
const sessionId = crypto.randomUUID()
await redis.set(sessionId, userId, 'EX', 3600)

// Verify
const userId = await redis.get(sessionId)`
          }
        },
        threats: [
          {
            name: 'XSS',
            attack: 'Steal token from JS',
            defense: 'HttpOnly cookies, CSP headers'
          },
          {
            name: 'CSRF',
            attack: 'Forge requests',
            defense: 'CSRF tokens, SameSite cookies'
          },
          {
            name: 'Brute Force',
            attack: 'Guess passwords',
            defense: 'Rate limiting, captcha, account lockout'
          },
          {
            name: 'Session Hijacking',
            attack: 'Steal session',
            defense: 'Secure flag, rotate tokens, fingerprinting'
          }
        ]
      }
    },
    {
      id: 4,
      name: 'Layer 4: Backend Implementation',
      color: 'text-purple-400',
      content: {
        passwordHandling: `
// NEVER store plain text
// NEVER use MD5/SHA1
// ALWAYS use bcrypt/argon2

// Hash on signup
const hashedPassword = await bcrypt.hash(password, 10)

// Verify on login
const valid = await bcrypt.compare(password, hashedPassword)`,

        sessionManagement: `
// Token generation
function generateTokens(userId) {
  const accessToken = jwt.sign(
    { userId, type: 'access' },
    ACCESS_SECRET,
    { expiresIn: '15m' }
  )

  const refreshToken = jwt.sign(
    { userId, type: 'refresh' },
    REFRESH_SECRET,
    { expiresIn: '7d' }
  )

  return { accessToken, refreshToken }
}

// Middleware
function authenticate(req, res, next) {
  const token = req.headers.authorization?.split(' ')[1]

  if (!token) return res.status(401).json({ error: 'No token' })

  try {
    const payload = jwt.verify(token, ACCESS_SECRET)
    req.userId = payload.userId
    next()
  } catch (error) {
    if (error.name === 'TokenExpiredError') {
      return res.status(401).json({ error: 'Token expired' })
    }
    return res.status(403).json({ error: 'Invalid token' })
  }
}`,

        databaseSchema: `
-- Users table
CREATE TABLE users (
  id TEXT PRIMARY KEY,
  email TEXT UNIQUE NOT NULL,
  password_hash TEXT NOT NULL,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  email_verified BOOLEAN DEFAULT FALSE,
  two_factor_enabled BOOLEAN DEFAULT FALSE
);

-- Sessions table
CREATE TABLE sessions (
  id TEXT PRIMARY KEY,
  user_id TEXT REFERENCES users(id),
  token_hash TEXT NOT NULL,
  expires_at TIMESTAMP NOT NULL,
  ip_address TEXT,
  user_agent TEXT,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Login attempts (for rate limiting)
CREATE TABLE login_attempts (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  email TEXT,
  ip_address TEXT,
  success BOOLEAN,
  attempted_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);`
      }
    },
    {
      id: 5,
      name: 'Layer 5: Universal Patterns',
      color: 'text-cyan-400',
      content: {
        corePattern: `
AUTH is fundamentally:
1. IDENTIFICATION: "Who are you?" (username/email)
2. AUTHENTICATION: "Prove it" (password/biometric)
3. AUTHORIZATION: "What can you do?" (roles/permissions)
4. ACCOUNTING: "What did you do?" (audit logs)`,

        universalFlow: `
// Every auth system ever:
async function authenticate(credentials) {
  // 1. Validate input
  if (!isValid(credentials)) throw 'Invalid input'

  // 2. Verify identity
  const user = await findUser(credentials.identifier)
  if (!user) throw 'User not found'

  // 3. Verify proof
  const valid = await verifyProof(credentials.proof, user.storedProof)
  if (!valid) throw 'Invalid credentials'

  // 4. Create session
  const session = await createSession(user)

  // 5. Return token
  return generateToken(session)
}`,

        atomicOperations: [
          'COMPARE: Does input match stored?',
          'GENERATE: Create unique identifier',
          'STORE: Save for later retrieval',
          'RETRIEVE: Get from storage',
          'EXPIRE: Invalidate after time',
          'REVOKE: Invalidate immediately'
        ],

        mathBehindIt: `
// Cryptographic foundations
HASH(password) → one-way transformation
SALT + HASH → prevents rainbow tables
HMAC(data, secret) → authenticated message
RSA/ECDSA → public/private key pairs

// Time-safety
constant_time_compare(a, b) → prevents timing attacks

// Randomness
crypto.randomBytes(32) → unpredictable tokens`
      }
    }
  ];

  const prompts = {
    simple: `
Create a login system with:
- Email/password authentication
- JWT tokens
- Protected routes
- Logout functionality`,

    detailed: `
Implement authentication with:
- Secure password hashing (bcrypt, 10 rounds)
- JWT access tokens (15min) and refresh tokens (7 days)
- Rate limiting (5 attempts per minute)
- CSRF protection
- XSS prevention
- Session management
- Password reset flow
- Email verification
- 2FA support`,

    complete: `
Build production auth system:

Frontend:
- Login/Signup forms with validation
- Protected route wrapper
- Token refresh interceptor
- Logout with cleanup
- Remember me option
- Social login buttons

Backend:
- POST /auth/login - authenticate user
- POST /auth/signup - create account
- POST /auth/refresh - refresh tokens
- POST /auth/logout - end session
- POST /auth/forgot - password reset
- POST /auth/verify - email verification
- GET /auth/me - current user

Security:
- bcrypt password hashing
- JWT with RS256
- Refresh token rotation
- Rate limiting per IP
- Account lockout after failures
- CSRF tokens
- Secure headers (CSP, HSTS)
- Audit logging

Database:
- Users table with constraints
- Sessions table with expiry
- Login attempts tracking
- Password reset tokens
- Email verification tokens`
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">🔐 AUTH: Complete Dissection</h1>
          <p className="text-gray-400">Understanding authentication from surface to core</p>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Left: Layer Navigation */}
          <div className="col-span-3 border border-green-400 p-4">
            <h2 className="text-xl mb-4">LAYERS</h2>
            <div className="space-y-2">
              {layers.map(layer => (
                <button
                  key={layer.id}
                  onClick={() => setCurrentLayer(layer.id)}
                  className={`w-full text-left p-2 border transition-all ${
                    currentLayer === layer.id
                      ? 'border-green-400 bg-green-900'
                      : 'border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <div className={`font-bold ${layer.color}`}>
                    Layer {layer.id}
                  </div>
                  <div className="text-xs text-gray-400">
                    {layer.name}
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-green-400">
              <h3 className="text-sm mb-2">THE PATTERN:</h3>
              <div className="text-xs text-gray-400 space-y-1">
                <div>1. Identify user</div>
                <div>2. Verify identity</div>
                <div>3. Create session</div>
                <div>4. Return token</div>
                <div>5. Check token on requests</div>
                <div>6. Refresh/expire</div>
              </div>
            </div>
          </div>

          {/* Middle: Layer Content */}
          <div className="col-span-6 border border-green-400 p-4">
            <h2 className="text-xl mb-4 border-b border-green-400 pb-2">
              <span className={layers[currentLayer].color}>
                {layers[currentLayer].name}
              </span>
            </h2>

            <div className="space-y-4 text-sm">
              {Object.entries(layers[currentLayer].content).map(([key, value]) => (
                <div key={key}>
                  <button
                    onClick={() => toggleSection(key)}
                    className="w-full text-left mb-2"
                  >
                    <h3 className="font-bold text-yellow-400 uppercase">
                      {expandedSections.has(key) ? '▼' : '▶'} {key.replace(/_/g, ' ')}
                    </h3>
                  </button>

                  {expandedSections.has(key) && (
                    <div className="ml-4">
                      {typeof value === 'string' ? (
                        <pre className="text-xs text-gray-300 overflow-x-auto">
                          {value}
                        </pre>
                      ) : Array.isArray(value) ? (
                        <div className="space-y-1">
                          {value.map((item, i) => (
                            <div key={i} className="text-xs text-gray-300">
                              {typeof item === 'string' ? (
                                item
                              ) : (
                                <div className="bg-gray-900 p-2 mb-2">
                                  {Object.entries(item).map(([k, v]) => (
                                    <div key={k}>
                                      <span className="text-green-400">{k}:</span>{' '}
                                      {Array.isArray(v) ? (
                                        <div className="ml-4">
                                          {v.map((vi, j) => (
                                            <div key={j}>• {vi}</div>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">{v}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(value).map(([k, v]) => (
                            <div key={k} className="bg-gray-900 p-2">
                              <div className="text-green-400 font-bold mb-1">{k}</div>
                              {typeof v === 'string' ? (
                                <pre className="text-xs text-gray-300">{v}</pre>
                              ) : (
                                <div className="text-xs text-gray-300">
                                  {Object.entries(v as any).map(([kk, vv]) => (
                                    <div key={kk}>
                                      <span className="text-yellow-400">{kk}:</span>{' '}
                                      {Array.isArray(vv) ? (
                                        vv.join(', ')
                                      ) : (
                                        <span>{vv}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Prompts & Implementation */}
          <div className="col-span-3 space-y-4">
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">PROMPTS</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-bold text-yellow-400 mb-2">SIMPLE</h3>
                  <pre className="text-xs text-gray-400 whitespace-pre-wrap">
{prompts.simple}
                  </pre>
                </div>

                <div>
                  <button
                    onClick={() => toggleSection('detailed-prompt')}
                    className="text-sm font-bold text-yellow-400 mb-2"
                  >
                    {expandedSections.has('detailed-prompt') ? '▼' : '▶'} DETAILED
                  </button>
                  {expandedSections.has('detailed-prompt') && (
                    <pre className="text-xs text-gray-400 whitespace-pre-wrap">
{prompts.detailed}
                    </pre>
                  )}
                </div>

                <div>
                  <button
                    onClick={() => toggleSection('complete-prompt')}
                    className="text-sm font-bold text-yellow-400 mb-2"
                  >
                    {expandedSections.has('complete-prompt') ? '▼' : '▶'} COMPLETE
                  </button>
                  {expandedSections.has('complete-prompt') && (
                    <pre className="text-xs text-gray-400 whitespace-pre-wrap">
{prompts.complete}
                    </pre>
                  )}
                </div>
              </div>
            </div>

            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">DECISIONS</h2>
              <div className="space-y-2 text-xs text-gray-400">
                <div className="flex justify-between">
                  <span>Token Type:</span>
                  <span className="text-green-400">JWT vs Session</span>
                </div>
                <div className="flex justify-between">
                  <span>Storage:</span>
                  <span className="text-green-400">Cookie vs LocalStorage</span>
                </div>
                <div className="flex justify-between">
                  <span>Hash Algorithm:</span>
                  <span className="text-green-400">bcrypt vs argon2</span>
                </div>
                <div className="flex justify-between">
                  <span>Token Lifetime:</span>
                  <span className="text-green-400">15min vs 1hour</span>
                </div>
                <div className="flex justify-between">
                  <span>Refresh Strategy:</span>
                  <span className="text-green-400">Rotate vs Reuse</span>
                </div>
                <div className="flex justify-between">
                  <span>2FA:</span>
                  <span className="text-green-400">TOTP vs SMS</span>
                </div>
              </div>
            </div>

            <div className="border border-red-400 p-4">
              <h2 className="text-xl mb-4 text-red-400">GOTCHAS</h2>
              <div className="space-y-1 text-xs text-gray-400">
                <div>• Storing passwords in plain text</div>
                <div>• Using MD5/SHA1 for passwords</div>
                <div>• Not using HTTPS</div>
                <div>• Tokens in URL parameters</div>
                <div>• No rate limiting</div>
                <div>• Predictable session IDs</div>
                <div>• Not rotating refresh tokens</div>
                <div>• Trusting client-side validation</div>
                <div>• Leaking info in errors</div>
                <div>• No audit logging</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: The Complete Understanding */}
        <div className="mt-6 border border-blue-400 p-4">
          <h2 className="text-xl text-blue-400 mb-2">THE COMPLETE UNDERSTANDING</h2>
          <div className="grid grid-cols-3 gap-4 text-sm text-gray-400">
            <div>
              <h3 className="text-green-400 mb-2">What AUTH Is:</h3>
              <p>A trust system. Users prove who they are, we believe them (or not), and remember our decision for a while.</p>
            </div>
            <div>
              <h3 className="text-yellow-400 mb-2">Why It's Complex:</h3>
              <p>Because attackers are creative, browsers are leaky, networks are insecure, and users pick "password123".</p>
            </div>
            <div>
              <h3 className="text-red-400 mb-2">How To Build It:</h3>
              <p>Pick proven patterns (JWT + bcrypt), implement carefully, test thoroughly, and never roll your own crypto.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}